import React from 'react';
import moment from 'moment';
import { isEmpty, isNil } from 'lodash';
import classnames from 'classnames';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import DateDisplay from '@bringg/react-components/dist/components/date-display/date-display';
import { TaskStatus, Tooltip } from '@bringg/react-components';
import { ToolTipPlacement } from '@bringg-frontend/gantt-timeline';
import type { Task, WayPoint } from '@bringg/types';
import type { TaskTooltipTranslations } from '../../../components/routes-planner/translations';
import { addDecimal } from '../../../utils/numbers-utils';
import './task-tooltip-info.scss';

export const TIME_FORMAT = moment.localeData().longDateFormat('LT');
const MAX_LENGTH = 48;

interface Props {
	taskToDisplay: Task;
	tooltipTranslations: TaskTooltipTranslations;
	taskStarted: boolean;
	timezone: string;
	color: string;
}

const getRequiredSkillsText = (requiredSkills: string[]) => {
	if (isEmpty(requiredSkills)) {
		return null;
	}
	const requiredSkillsText = requiredSkills.join(', ');
	if (requiredSkillsText.length > MAX_LENGTH) {
		return (
			<Tooltip
				color="white"
				placement={ToolTipPlacement.Right}
				title={<span className="hover-tooltip">{requiredSkillsText}</span>}
			>
				<span className="required-skills">{`${requiredSkillsText.slice(0, MAX_LENGTH)}...`}</span>
			</Tooltip>
		);
	}
	return requiredSkillsText;
};

const TaskTooltipInfo = (props: Props) => {
	const { taskToDisplay, tooltipTranslations, taskStarted, color, timezone } = props;

	const activeWP = taskToDisplay?.way_points?.[0] || ({} as WayPoint);

	const getGeneralInfoItem = (
		icon: BringgFontIcons,
		color: string,
		content: React.ReactNode | string,
		classNames?: string
	) => {
		return (
			<div className="task-tooltip-body-content-row">
				<BringgIcon
					className="task-tooltip-body-content-icon"
					iconName={icon}
					style={{ '--color': color } as React.CSSProperties}
				/>
				<div className={classnames(classNames)}>{content}</div>
			</div>
		);
	};

	return (
		<>
			{activeWP.customer?.name &&
				getGeneralInfoItem(
					BringgFontIcons.User,
					color,
					<>
						<div data-test-id="tooltip-customer-name" className="task-tooltip-body-content-customer-name">
							{activeWP.customer?.name}
						</div>

						{activeWP.address && (
							<div data-test-id="tooltip-address" className="task-tooltip-body-content-address">
								{activeWP.address}
							</div>
						)}
						{activeWP.address_second_line && (
							<div data-test-id="tooltip-address-second-line">{activeWP.address_second_line}</div>
						)}
					</>
				)}
			{((activeWP.no_earlier_than && activeWP.no_later_than) || activeWP.scheduled_at) &&
				getGeneralInfoItem(
					BringgFontIcons.Clock,
					color,
					<>
						{activeWP.no_earlier_than && activeWP.no_later_than && (
							<div data-test-id="tooltip-time-window">
								<DateDisplay date={activeWP.no_earlier_than} format={TIME_FORMAT} timezone={timezone} />
								<span> - </span>
								<DateDisplay date={activeWP.no_later_than} format={TIME_FORMAT} timezone={timezone} />
							</div>
						)}
						{activeWP.scheduled_at && (
							<div data-test-id="tooltip-scheduled-at">
								{tooltipTranslations.scheduledFor}:{' '}
								<DateDisplay date={activeWP.scheduled_at} format={TIME_FORMAT} timezone={timezone} />
							</div>
						)}
					</>
				)}
			{!isEmpty(taskToDisplay.required_skills) &&
				getGeneralInfoItem(
					BringgFontIcons.Checklist,
					color,
					getRequiredSkillsText(taskToDisplay.required_skills),
					'task-tooltip-body-content-required-skills'
				)}
			{!isNil(taskToDisplay.service_plan) &&
				getGeneralInfoItem(
					BringgFontIcons.Ticket,
					color,
					<>{taskToDisplay.service_plan.name}</>,
					'task-tooltip-body-content-service-plan'
				)}

			{!isNil(taskToDisplay.aggregations?.max_weight) &&
				getGeneralInfoItem(
					BringgFontIcons.Weight,
					color,
					<>{addDecimal(taskToDisplay.aggregations.max_weight)}</>,
					'task-tooltip-body-content-total-weight'
				)}

			{taskStarted && (
				<div className="task-tooltip-body-content-row">
					<TaskStatus status={taskToDisplay.status} translations={tooltipTranslations.taskStatus} />
				</div>
			)}
		</>
	);
};

export default TaskTooltipInfo;
